import { Controller } from '@hotwired/stimulus'
import FocusTrapController from './focus-trap'
import FormChangeController from './form-change'

export default class DrawerController extends Controller<HTMLDialogElement> {
  declare depthValue: number
  declare exitingValue: boolean
  declare headerInfoVisibleValue: boolean
  declare indexValue: number
  declare readonly focusTrapOutlet: FocusTrapController
  declare readonly formChangeOutlet: FormChangeController
  declare readonly hasFocusTrapOutlet: boolean
  declare readonly hasFormChangeOutlet: boolean

  static outlets = ['focus-trap', 'form-change']
  static values = {
    depth: Number,
    exiting: Boolean,
    headerInfoVisible: Boolean,
    index: Number,
  }

  // Lifecycle methods

  disconnect() {
    if (this.hasFocusTrapOutlet) this.focusTrapOutlet.deactivate()
  }

  // Public methods

  close(event: Event) {
    if (!this.#isCloseable()) {
      event.preventDefault()
      this.dispatch('close-prevented')
      return
    }

    this.exitingValue = true
    this.dispatch('close')

    setTimeout(() => {
      this.element.close()
      this.element.remove()
      this.dispatch('closed')
    }, 300)
  }

  dismiss() {
    // The animated close() path also fires a native `close` event (via
    // this.element.close()); it sets exitingValue first, so skip those. Only
    // handle a direct browser light-dismiss (e.g. Safari backdrop click), which
    // bypasses close() and leaves the element in the DOM.
    if (this.exitingValue) return

    this.exitingValue = true
    this.dispatch('close')
    // Mirror close()'s teardown: close() before remove() so the dialog's
    // `open` state and its DOM presence clear together (the backdrop CSS keys
    // on `.new-drawer[open]`). Setting exitingValue first makes the native
    // `close` this fires re-enter as a no-op.
    this.element.close()
    this.element.remove()
    this.dispatch('closed')
  }

  open() {
    this.element.showModal()
    if (this.hasFocusTrapOutlet) this.focusTrapOutlet.activate()
    this.dispatch('opened')
  }

  toggleHeaderInfo(event: Event) {
    const toggler = event.target as HTMLElement
    toggler.ariaExpanded = String(this.headerInfoVisibleValue)
    this.headerInfoVisibleValue = !this.headerInfoVisibleValue
  }

  // Private methods

  #isCloseable() {
    if (!this.hasFormChangeOutlet) return true
    const { modifiedValue, submittedValue } = this.formChangeOutlet
    if (submittedValue || !modifiedValue) return true
    return window.confirm('You have unsaved changes. Closing this drawer will discard them. Are you sure?')
  }
}
